import {
    Box,
    Flex,
    FormControl,
    FormLabel,
    FormLabelProps,
    Input,
    Tag,
    TagCloseButton,
    TagLabel,
    Text,
} from '@chakra-ui/react';
import { useState } from 'react';
import { INVENTORY_FIELD_FOCUS_COLOR } from '../../utils/inventoryFieldStyles';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface PartFulfillmentContactsFieldProps {
    value: Array<string>;
    onChange: (emails: Array<string>) => void;
    isDisabled?: boolean;
    label?: string;
    labelProps?: FormLabelProps;
    placeholder?: string;
}

/**
 * Freeform chip field for part fulfillment emails.
 * Mirrors inventory tag chrome without a search API.
 */
const PartFulfillmentContactsField = ({
    value,
    onChange,
    isDisabled = false,
    label = '',
    labelProps,
    placeholder = 'Add email…',
}: PartFulfillmentContactsFieldProps) => {
    const [query, setQuery] = useState('');
    const [error, setError] = useState<string | undefined>();

    const tryAdd = (raw: string) => {
        const email = raw.trim();
        if (!email) {
            setQuery('');
            setError(undefined);
            return;
        }
        if (!EMAIL_PATTERN.test(email)) {
            setError('Enter a valid email.');
            return;
        }
        if (value.some((v) => v.trim().toLowerCase() === email.toLowerCase())) {
            setError('Email already added.');
            return;
        }
        onChange([...value, email]);
        setQuery('');
        setError(undefined);
    };

    const remove = (email: string) => {
        onChange(value.filter((v) => v !== email));
        setError(undefined);
    };

    return (
        <FormControl minW={0} w="100%">
            {label ? (
                <FormLabel fontSize="sm" {...labelProps}>
                    {label}
                </FormLabel>
            ) : null}
            <Flex
                align="center"
                gap={2}
                flexWrap="wrap"
                w="100%"
                minW={0}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="lg"
                bg="white"
                px={2}
                py={1}
                minH="40px"
                transition="border-color 0.15s ease, box-shadow 0.15s ease"
                _focusWithin={{
                    borderColor: INVENTORY_FIELD_FOCUS_COLOR,
                    boxShadow: 'none',
                }}
            >
                {value.map((email) => (
                    <Tag
                        key={email}
                        size="sm"
                        borderRadius="md"
                        colorScheme="purple"
                        variant="subtle"
                        flexShrink={0}
                    >
                        <TagLabel>{email}</TagLabel>
                        {!isDisabled && (
                            <TagCloseButton
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => remove(email)}
                            />
                        )}
                    </Tag>
                ))}
                <Box flex={1} minW="120px" display="flex" alignItems="center" minH="24px">
                    <Input
                        size="sm"
                        variant="unstyled"
                        type="email"
                        h="24px"
                        lineHeight="24px"
                        py={0}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setError(undefined);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                tryAdd(query);
                            }
                        }}
                        onBlur={() => {
                            if (query.trim()) tryAdd(query);
                        }}
                        placeholder={value.length === 0 ? placeholder : 'Add another…'}
                        isDisabled={isDisabled}
                    />
                </Box>
            </Flex>
            {error ? (
                <Text fontSize="xs" color="red.500" mt={1}>
                    {error}
                </Text>
            ) : null}
        </FormControl>
    );
};

export default PartFulfillmentContactsField;
